import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { DatePicker } from "../components/DatePicker";
import { BRAND_COLORS, STATUS_COLORS } from "../lib/bookingStatus";
import { bumpUnreadTeamQuotes } from "../lib/unreadTeamQuotes";
import { deDate } from "../lib/dateFormat";
import { deCurrency } from "../lib/numberFormat";

// Feb 2026 · User-Directive (3 Sep 2026) — die 9 Pflicht-Abschnitte
// eines Weissblom-Angebots werden im PDF gerendert; das Formular hält
// dieselben Defaults synchron. Beim Speichern befüllt das Backend die
// Angebotsnummer (WB-AN-YYYY-####) und rechnet Brutto/Netto/USt.
const DEFAULT_PAYMENT_TERMS = "Zahlung innerhalb von 14 Tagen netto ohne Abzüge.";
const DEFAULT_TERMS_REFERENCE = "Es gelten unsere Allgemeinen Geschäftsbedingungen, einsehbar unter weissblom.de/agb.";
const genLineId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `li-${Math.random().toString(36).slice(2)}-${Date.now()}`;

const EMPTY_ITEM = { label: "", qty: 1, unit_price: 0, unit: "" };
const newItem = () => ({ _uid: genLineId(), ...EMPTY_ITEM });

function money(n, cur = "EUR") {
  return deCurrency(n || 0, cur);
}

export default function TeamQuotes() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [customersById, setCustomersById] = useState({});
  const [customersList, setCustomersList] = useState([]);
  const [company, setCompany] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showForward, setShowForward] = useState(false);
  const [convertingId, setConvertingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [forwardSubmitting, setForwardSubmitting] = useState(false);
  // Feb 2026 · User-Directive „Filter-Chips" (3 Sep 2026): Client-side
  // Filter über allen Angeboten. Kein Angebot wird ausgeblendet — der
  // Chip bestimmt nur die aktuelle Sichtbarkeits-Untermenge. Default
  // „all", damit die Directive „nichts verstecken" beim Seiten-Öffnen
  // gilt. Werte: "all" | "open" | "approved" | "declined".
  const [statusFilter, setStatusFilter] = useState("all");
  const [forwardForm, setForwardForm] = useState({
    customer_id: "",
    property_id: "",
    title: "",
    admin_message: "",
    valid_until: "",
    file: null,
  });
  const [form, setForm] = useState({
    property_id: "",
    title: "",
    description: "",
    intro_text: "",
    valid_until: "",
    vat_rate: 19,
    payment_terms: "",
    terms_reference: "",
    signature_name: "",
    recipient_name: "",
    recipient_address_lines: "",
    line_items: [newItem()],
  });

  const load = async () => {
    const [q, p, cs, cu] = await Promise.all([
      api.get("/quotes"),
      api.get("/properties"),
      api.get("/team/company-settings").catch(() => ({ data: null })),
      api.get("/team/customers").catch(() => ({ data: [] })),
    ]);
    setRows(q.data);
    setProperties(p.data);
    setCompany(cs.data);
    setCustomersList(cu.data || []);
    setCustomersById(Object.fromEntries((cu.data || []).map((c) => [c.id, c])));
  };
  useEffect(() => { load(); }, []);

  const convertToInvoice = async (quote) => {
    const vatIn = window.prompt(`Rechnung für „${quote.title}" erstellen.\nUSt.-Satz in % (z. B. 19, leer für 0):`, "19");
    if (vatIn === null) return;
    const vat_rate = parseFloat((vatIn || "0").replace(",", ".")) || 0;
    setConvertingId(quote.id);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/convert-to-invoice`, { vat_rate });
      toast.success(`Rechnung ${data.number} angelegt.`);
      await load();
      nav("/team/invoices");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setConvertingId(null);
    }
  };

  const openPdf = (quoteId) => {
    // Nutzt denselben Browser-Session-Cookie wie die App — Response ist
    // `Content-Disposition: inline`, also öffnet im Tab statt Download.
    const base = process.env.REACT_APP_BACKEND_URL || "";
    window.open(`${base}/api/quotes/${quoteId}/pdf`, "_blank", "noopener");
    // Feb 2026 · User-Directive „Admin-Badge auf Angebote-Nav" (3 Sep 2026):
    // Ein Klick auf „PDF öffnen" gilt als „gesehen" — wir schicken das
    // Angebot in den mark-seen-Endpoint und stoßen sofort den Badge-
    // Refresh an, damit der Nav-Zähler fällt.
    api.post(`/quotes/team/${quoteId}/mark-seen`).catch(() => {}).finally(bumpUnreadTeamQuotes);
  };

  // Beim ersten Laden der Liste markieren wir NICHTS automatisch als
  // gesehen — der Admin muss aktiv ein Angebot öffnen. Ausnahme: der
  // „Alles gelesen"-Button ganz oben (siehe Render). Die Funktion setzt
  // ausserdem den Badge sofort auf 0, damit die UI nicht auf den Poll
  // wartet.
  const markAllTeamQuotesSeen = async () => {
    try {
      await api.post("/quotes/team/mark-all-seen");
      toast.success("Alle Angebote als gelesen markiert.");
      bumpUnreadTeamQuotes();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  // Automatisch Empfänger + Objekt-Adresse ins Formular ziehen, sobald ein
  // Objekt gewählt wird (Admin muss die 9 Pflichtabschnitte nicht doppelt tippen).
  useEffect(() => {
    if (!form.property_id) return;
    const prop = properties.find((p) => p.id === form.property_id);
    if (!prop) return;
    const owner = customersById[prop.owner_id];
    const propAddr = [prop.address, [prop.zip, prop.city].filter(Boolean).join(" "), prop.country].filter(Boolean).join("\n");
    setForm((f) => ({
      ...f,
      recipient_name: f.recipient_name.trim() || owner?.name || owner?.email || "",
      recipient_address_lines: f.recipient_address_lines.trim() || propAddr,
    }));
  }, [form.property_id, properties, customersById]);

  const subtotal = useMemo(
    () => form.line_items.reduce((s, li) => s + (parseFloat(li.qty) || 0) * (parseFloat(li.unit_price) || 0), 0),
    [form.line_items],
  );

  // Feb 2026 · Filter-Chip-Zähler + gefilterte Liste. Wird über der
  // Angebots-Tabelle als Segmented-Control gerendert.
  const statusCounts = useMemo(() => ({
    all:      rows.length,
    open:     rows.filter((r) => r.status === "sent" || r.status === "viewed").length,
    approved: rows.filter((r) => r.status === "approved").length,
    declined: rows.filter((r) => r.status === "declined").length,
  }), [rows]);
  const filteredRows = useMemo(() => {
    if (statusFilter === "all") return rows;
    if (statusFilter === "open") return rows.filter((r) => r.status === "sent" || r.status === "viewed");
    return rows.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);
  const vatRate = parseFloat(form.vat_rate) || 0;
  const vatAmount = Math.round(subtotal * (vatRate / 100) * 100) / 100;
  const gross = Math.round((subtotal + vatAmount) * 100) / 100;

  const submit = async (e) => {
    e.preventDefault();
    const cleanItems = form.line_items
      .filter((li) => li.label && li.unit_price)
      .map((li) => ({
        label: li.label,
        qty: parseFloat(li.qty) || 1,
        unit_price: parseFloat(li.unit_price) || 0,
        unit: li.unit || null,
      }));
    if (!cleanItems.length) return toast.error("Mindestens eine Position mit Preis erforderlich.");
    const address_lines = form.recipient_address_lines
      .split("\n").map((ln) => ln.trim()).filter(Boolean);
    setSubmitting(true);
    try {
      const payload = {
        property_id: form.property_id,
        title: form.title,
        description: form.description || null,
        intro_text: form.intro_text || null,
        line_items: cleanItems,
        currency: "EUR",
        valid_until: form.valid_until || null,
        vat_rate: vatRate,
        payment_terms: form.payment_terms || null,
        terms_reference: form.terms_reference || null,
        signature_name: form.signature_name || null,
        recipient_name: form.recipient_name || null,
        recipient_address_lines: address_lines,
      };
      const { data } = await api.post("/quotes", payload);
      toast.success(`Angebot ${data.number} erstellt und an Kunden gesendet.`);
      setShowForm(false);
      setForm({
        property_id: "", title: "", description: "", intro_text: "",
        valid_until: "", vat_rate: 19, payment_terms: "",
        terms_reference: "", signature_name: "",
        recipient_name: "", recipient_address_lines: "",
        line_items: [newItem()],
      });
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const addRow = () => setForm({ ...form, line_items: [...form.line_items, newItem()] });
  const rmRow  = (i) => setForm({ ...form, line_items: form.line_items.filter((_, idx) => idx !== i) });
  const setRow = (i, patch) => setForm({ ...form, line_items: form.line_items.map((li, idx) => idx === i ? { ...li, ...patch } : li) });

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-quotes">
      <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
        <div>
          <div className="label-caps mb-3">Weissblom Team</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy">Angebote</h1>
          <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">
            Angebote mit Positionen und Preisen erstellen — automatisch generierte Angebotsnummer,
            Netto/USt/Brutto, Gültigkeitsdauer, Zahlungsbedingungen und Unterschriftsfeld.
            Der Kunde bekommt das PDF per E-Mail und kann direkt zustimmen.
          </p>
        </div>
        <button data-testid="new-quote-btn" onClick={() => { setShowForm((v) => !v); setShowForward(false); }}
          className="text-ivory px-8 py-3 text-[12px] tracking-[0.15em] uppercase hover:opacity-90"
          style={{ background: BRAND_COLORS.navy }}>{showForm ? "Schließen" : "Neues Angebot"}</button>
        <button data-testid="forward-quote-btn" onClick={() => { setShowForward((v) => !v); setShowForm(false); }}
          className="border border-navy text-navy px-8 py-3 text-[12px] tracking-[0.15em] uppercase hover:bg-navy hover:text-ivory transition-colors">{showForward ? "Schließen" : "Angebot weiterleiten"}</button>
      </div>

      {showForward && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!forwardForm.file) return toast.error("Bitte ein PDF auswählen.");
            if (!forwardForm.customer_id) return toast.error("Bitte einen Kunden wählen.");
            if (!forwardForm.title.trim()) return toast.error("Bitte einen Titel angeben.");
            setForwardSubmitting(true);
            try {
              const fd = new FormData();
              fd.append("file", forwardForm.file);
              fd.append("customer_id", forwardForm.customer_id);
              fd.append("title", forwardForm.title.trim());
              if (forwardForm.property_id) fd.append("property_id", forwardForm.property_id);
              if (forwardForm.admin_message) fd.append("admin_message", forwardForm.admin_message);
              if (forwardForm.valid_until) fd.append("valid_until", forwardForm.valid_until);
              const { data } = await api.post("/quotes/forward", fd, { headers: { "Content-Type": "multipart/form-data" } });
              toast.success(`Angebot ${data.number} an Kunden weitergeleitet.`);
              setShowForward(false);
              setForwardForm({ customer_id: "", property_id: "", title: "", admin_message: "", valid_until: "", file: null });
              await load();
            } catch (err) {
              toast.error(formatErr(err.response?.data?.detail) || err.message);
            } finally {
              setForwardSubmitting(false);
            }
          }}
          data-testid="forward-quote-form"
          className="bg-white border border-navy/10 p-6 md:p-8 mb-8 space-y-5"
        >
          <div className="border-b border-navy/10 pb-4 mb-2">
            <div className="label-caps mb-1">Fremd-Angebot weiterleiten</div>
            <p className="text-[12px] text-navy-dark/60 leading-relaxed">
              Laden Sie ein bestehendes PDF-Angebot einer Drittfirma hoch. Der Kunde bekommt es per E-Mail
              mit Anhang und kann direkt in der App entscheiden. Status-Änderungen erscheinen automatisch hier.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="label-caps mb-2">Empfänger · Kunde</div>
              <select required data-testid="fwd-customer-select" value={forwardForm.customer_id}
                onChange={(e) => setForwardForm({ ...forwardForm, customer_id: e.target.value, property_id: "" })}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3">
                <option value="">Kunden wählen</option>
                {customersList.map((c) => (
                  <option key={c.id} value={c.id}>{c.name || c.email}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="label-caps mb-2">Objekt (optional)</div>
              <select data-testid="fwd-property-select" value={forwardForm.property_id}
                onChange={(e) => setForwardForm({ ...forwardForm, property_id: e.target.value })}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3">
                <option value="">Kein Objektbezug</option>
                {properties
                  .filter((p) => !forwardForm.customer_id || p.owner_id === forwardForm.customer_id)
                  .map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>

          <input required data-testid="fwd-title-input" placeholder='Titel des Angebots (z. B. "Pool-Grundreinigung — Fa. Aqua")'
            value={forwardForm.title}
            onChange={(e) => setForwardForm({ ...forwardForm, title: e.target.value })}
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3" />

          <textarea rows={3} data-testid="fwd-message-input"
            placeholder="Optionale Nachricht an den Kunden (erscheint in der E-Mail über dem PDF)"
            value={forwardForm.admin_message}
            onChange={(e) => setForwardForm({ ...forwardForm, admin_message: e.target.value })}
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-[13px]" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="label-caps mb-2">Gültig bis (optional)</div>
              <DatePicker testid="fwd-valid-until" placeholder="Datum wählen"
                value={forwardForm.valid_until} onChange={(v) => setForwardForm({ ...forwardForm, valid_until: v })} />
            </div>
            <div>
              <div className="label-caps mb-2">PDF-Anhang</div>
              <input required type="file" accept="application/pdf,.pdf" data-testid="fwd-file-input"
                onChange={(e) => setForwardForm({ ...forwardForm, file: e.target.files?.[0] || null })}
                className="w-full text-[12px] file:mr-3 file:px-3 file:py-1.5 file:border file:border-navy/30 file:bg-white file:text-navy file:text-[11px] file:tracking-wider file:uppercase file:cursor-pointer hover:file:bg-navy hover:file:text-ivory" />
              {forwardForm.file && (
                <div className="text-[11px] text-navy-dark/60 mt-1.5" data-testid="fwd-file-name">
                  {forwardForm.file.name} · {(forwardForm.file.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>
          </div>

          <button data-testid="forward-submit-btn" type="submit" disabled={forwardSubmitting}
            className="w-full text-ivory py-3 text-[12px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-50"
            style={{ background: BRAND_COLORS.navy }}>
            {forwardSubmitting ? "Wird versendet …" : "PDF an Kunden versenden"}
          </button>
        </form>
      )}

      {showForm && (
        <form onSubmit={submit} data-testid="quote-form" className="bg-white border border-navy/10 p-6 md:p-8 mb-8 space-y-6">
          {/* ---- 1. Kopfbereich (Firmenlogo · Kontakt · Steuernummer) ---- */}
          <section className="border-b border-navy/10 pb-5">
            <div className="label-caps mb-2">Kopfbereich · Weissblom-Daten</div>
            <div className="text-[12px] text-navy-dark/70 leading-relaxed" data-testid="quote-company-preview">
              {company ? (
                <>
                  <b className="text-navy">{company.name}</b> · {company.street} · {company.zip_city} · {company.country}<br />
                  Tel. {company.phone} · E-Mail {company.email} · Web {company.web}<br />
                  Steuer-Nr. {company.tax_number} · USt.-ID {company.vat_id} · Geschäftsführung {company.director}
                </>
              ) : (
                <span className="italic">Firmendaten werden geladen …</span>
              )}
            </div>
            <div className="text-[11px] text-navy-dark/50 mt-2">
              Änderungen an diesen Daten laufen zentral über <b>Zugänge → Firma bearbeiten</b> und
              werden automatisch in jedes neue PDF übernommen.
            </div>
          </section>

          {/* ---- 2. Grunddaten: Objekt · Gültigkeit · Titel · Einleitung ---- */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="label-caps mb-2">Objekt</div>
              <select required data-testid="q-property-select" value={form.property_id}
                onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3">
                <option value="">Objekt wählen</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <div className="label-caps mb-2">Gültig bis</div>
              <DatePicker testid="q-valid-until" placeholder="Datum wählen"
                value={form.valid_until} onChange={(v) => setForm({ ...form, valid_until: v })} />
            </div>
          </section>
          <input required data-testid="q-title-input" placeholder="Titel des Angebots" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3" />
          <textarea rows={3} placeholder="Einleitungstext (erscheint über der Positions-Tabelle)"
            data-testid="q-intro-input"
            value={form.intro_text}
            onChange={(e) => setForm({ ...form, intro_text: e.target.value })}
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3" />

          {/* ---- 3. Empfänger ---- */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-navy/10">
            <div>
              <div className="label-caps mb-2">Empfänger · Name</div>
              <input data-testid="q-recipient-name" value={form.recipient_name}
                onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                placeholder="Wird aus dem Objekt vorbelegt"
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3" />
            </div>
            <div>
              <div className="label-caps mb-2">Adresse (mehrzeilig)</div>
              <textarea rows={3} data-testid="q-recipient-address" value={form.recipient_address_lines}
                onChange={(e) => setForm({ ...form, recipient_address_lines: e.target.value })}
                placeholder="Straße Nr.&#10;PLZ Ort&#10;Land"
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-[13px]" />
            </div>
          </section>

          {/* ---- 4. Leistungsbeschreibung ---- */}
          <section className="pt-2 border-t border-navy/10">
            <div className="label-caps mb-3">Positionen</div>
            <div className="space-y-2">
              {form.line_items.map((li, i) => (
                <div key={li._uid} className="grid grid-cols-12 gap-2">
                  <input required data-testid={`li-label-${i}`} placeholder="Position" value={li.label}
                    onChange={(e) => setRow(i, { label: e.target.value })}
                    className="col-span-5 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px]" />
                  <input data-testid={`li-qty-${i}`} type="number" step="0.01" placeholder="Menge" value={li.qty}
                    onChange={(e) => setRow(i, { qty: e.target.value })}
                    className="col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-right" />
                  <input data-testid={`li-unit-${i}`} placeholder="Einheit" value={li.unit}
                    onChange={(e) => setRow(i, { unit: e.target.value })}
                    className="col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px]" />
                  <input required data-testid={`li-price-${i}`} type="number" step="0.01" placeholder="EP" value={li.unit_price}
                    onChange={(e) => setRow(i, { unit_price: e.target.value })}
                    className="col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-right" />
                  <button type="button" onClick={() => rmRow(i)}
                    className="col-span-1 text-navy-dark/50 hover:text-red-600">✕</button>
                </div>
              ))}
            </div>
            <button type="button" data-testid="add-li-btn" onClick={addRow}
              className="mt-3 text-[11px] tracking-widest uppercase text-navy border-b border-gold hover:text-gold">+ Position hinzufügen</button>
          </section>

          {/* ---- 5. Gesamtsumme (Netto · USt · Brutto) ---- */}
          <section className="pt-4 border-t border-navy/10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <div className="label-caps mb-2">USt.-Satz (%)</div>
                <input data-testid="q-vat-rate" type="number" step="0.1" min="0" value={form.vat_rate}
                  onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
                  className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-right" />
              </div>
              <div className="md:col-span-2 flex flex-col items-end justify-end gap-1 text-[13px]">
                <div className="flex justify-between w-full max-w-xs text-navy-dark/70">
                  <span>Netto</span>
                  <span data-testid="quote-preview-subtotal" className="tabular-nums">{money(subtotal)}</span>
                </div>
                <div className="flex justify-between w-full max-w-xs text-navy-dark/70">
                  <span>USt. {vatRate ? `${vatRate}%` : "0%"}</span>
                  <span data-testid="quote-preview-vat" className="tabular-nums">{money(vatAmount)}</span>
                </div>
                <div className="flex justify-between w-full max-w-xs pt-1 mt-1 border-t border-navy/15 font-display text-xl text-navy">
                  <span>Brutto</span>
                  <span data-testid="quote-preview-gross" className="tabular-nums">{money(gross)}</span>
                </div>
              </div>
            </div>
          </section>

          {/* ---- 7 + 8. Zahlungsbedingungen + AGB-Verweis ---- */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-navy/10">
            <div>
              <div className="label-caps mb-2">Zahlungsbedingungen</div>
              <input data-testid="q-payment-terms" value={form.payment_terms}
                onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                placeholder={DEFAULT_PAYMENT_TERMS}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-[13px]" />
            </div>
            <div>
              <div className="label-caps mb-2">AGB-Verweis</div>
              <input data-testid="q-terms-reference" value={form.terms_reference}
                onChange={(e) => setForm({ ...form, terms_reference: e.target.value })}
                placeholder={DEFAULT_TERMS_REFERENCE}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-[13px]" />
            </div>
          </section>

          {/* ---- 9. Unterschrift · Vertretungsberechtigter ---- */}
          <section className="pt-2 border-t border-navy/10">
            <div className="label-caps mb-2">Unterschrift · Vertretungsberechtigter</div>
            <input data-testid="q-signature-name" value={form.signature_name}
              onChange={(e) => setForm({ ...form, signature_name: e.target.value })}
              placeholder={company?.director || "Name des Vertretungsberechtigten"}
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-[13px]" />
            <div className="text-[11px] text-navy-dark/50 mt-2">
              Leer lassen, um automatisch die Geschäftsführung aus den Firmen-Stammdaten zu übernehmen.
            </div>
          </section>

          <button data-testid="quote-submit-btn" type="submit" disabled={submitting}
            className="w-full text-ivory py-3 text-[12px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-50"
            style={{ background: BRAND_COLORS.navy }}>
            {submitting ? "Wird gesendet …" : "Angebot erstellen & an Kunden senden"}
          </button>
        </form>
      )}

      <div className="bg-white border border-navy/10">
        {/* Feb 2026 · User-Directive „Filter-Chips" (3 Sep 2026):
            Segmented-Control mit den 4 Status-Chips. Sichtbarkeit bleibt
            garantiert — Default „Alle". Aktiver Chip: Navy-Fill; inaktive:
            Outline. Zähler pro Chip als kleine Tabular-Zahl. */}
        <div
          className="flex flex-wrap items-center gap-2 p-3 border-b border-navy/10"
          data-testid="team-quotes-filter-bar"
          role="tablist"
          aria-label="Angebote nach Status filtern"
        >
          {[
            { key: "all",      label: "Alle",       icon: null,          color: BRAND_COLORS.navy },
            { key: "open",     label: "Nur offen",  icon: Clock,         color: BRAND_COLORS.gold },
            { key: "approved", label: "Genehmigt",  icon: CheckCircle2,  color: BRAND_COLORS.greenDeep },
            { key: "declined", label: "Abgelehnt",  icon: XCircle,       color: STATUS_COLORS.red },
          ].map((c) => {
            const active = statusFilter === c.key;
            const Icon = c.icon;
            return (
              <button
                key={c.key}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`team-quotes-filter-${c.key}`}
                onClick={() => setStatusFilter(c.key)}
                className={
                  "inline-flex items-center gap-2 px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase border transition-colors " +
                  (active
                    ? "text-ivory"
                    : "bg-white text-navy hover:bg-navy/5")
                }
                style={
                  active
                    ? { background: c.color, borderColor: c.color }
                    : { borderColor: `${c.color}55`, color: c.color }
                }
              >
                {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />}
                <span>{c.label}</span>
                <span
                  className={
                    "tabular-nums text-[10.5px] px-1.5 py-0.5 rounded-sm normal-case tracking-normal " +
                    (active ? "bg-white/20" : "bg-navy/5")
                  }
                  data-testid={`team-quotes-filter-${c.key}-count`}
                >
                  {statusCounts[c.key]}
                </span>
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-12 gap-4 p-4 border-b border-navy/10 label-caps text-[11px]">
          <div className="col-span-2">Angebots-Nr.</div>
          <div>Datum</div>
          <div className="col-span-3">Titel</div>
          <div className="col-span-2">Objekt</div>
          <div>Brutto</div>
          <div>Status</div>
          <div className="col-span-2">Aktionen</div>
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-[13px] text-navy-dark/50 text-center">Noch keine Angebote erstellt.</div>
        ) : filteredRows.length === 0 ? (
          <div
            className="p-8 text-[13px] text-navy-dark/50 text-center"
            data-testid="team-quotes-empty-filter"
          >
            Keine Angebote mit diesem Status. Klicken Sie „Alle", um alle Angebote wieder anzuzeigen.
          </div>
        ) : filteredRows.map((r) => (
          <div key={r.id} data-testid={`team-quote-row-${r.id}`}
            className="grid grid-cols-12 gap-4 p-4 border-b border-navy/5 last:border-b-0 text-[13px] items-center">
            <div className="col-span-2 text-navy-dark font-medium tabular-nums" data-testid={`quote-number-${r.id}`}>{r.number || "—"}</div>
            <div className="text-navy-dark">{deDate(r.created_at)}</div>
            <div className="col-span-3 text-navy truncate">{r.title}</div>
            <div className="col-span-2 text-navy-dark truncate">{r.property_name}</div>
            <div className="text-navy font-medium tabular-nums">
              {r.type === "forwarded" ? (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-wider uppercase border border-gold/50 text-gold"
                  data-testid={`quote-type-forwarded-${r.id}`}
                >Weitergeleitet</span>
              ) : (
                money(r.total, r.currency)
              )}
            </div>
            <div className="label-caps text-[10.5px]" data-testid={`quote-status-cell-${r.id}`}>
              {/* Feb 2026 · User-Directive „Symbol pro Status in der
                  Listenzeile" (3 Sep 2026): Grüner Haken · rotes Kreuz ·
                  graues Uhr-Symbol — direkt sichtbar ohne Detailansicht.
                  Label bleibt aus Barrierefreiheits-Gründen daneben. */}
              {r.status === "approved" ? (
                <span
                  className="inline-flex items-center gap-1.5"
                  style={{ color: BRAND_COLORS.greenDeep }}
                  data-testid={`quote-status-icon-approved-${r.id}`}
                  title="Genehmigt"
                >
                  <CheckCircle2 className="w-4 h-4" strokeWidth={2} aria-hidden />
                  <span>Genehmigt</span>
                </span>
              ) : r.status === "declined" ? (
                <span
                  className="inline-flex items-center gap-1.5"
                  style={{ color: STATUS_COLORS.red }}
                  data-testid={`quote-status-icon-declined-${r.id}`}
                  title="Abgelehnt"
                >
                  <XCircle className="w-4 h-4" strokeWidth={2} aria-hidden />
                  <span>Abgelehnt</span>
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5"
                  style={{ color: BRAND_COLORS.gold }}
                  data-testid={`quote-status-icon-pending-${r.id}`}
                  title={r.status === "viewed" ? "Angesehen · noch keine Entscheidung" : "Neu · noch keine Entscheidung"}
                >
                  <Clock className="w-4 h-4" strokeWidth={2} aria-hidden />
                  <span>{r.status === "viewed" ? "Angesehen" : "Neu"}</span>
                </span>
              )}
              {r.decided_at && <div className="text-[10px] text-navy-dark/50 normal-case tracking-normal mt-1">{deDate(r.decided_at)}</div>}
            </div>
            <div className="col-span-2 flex flex-col gap-1.5">
              <button
                onClick={() => openPdf(r.id)}
                data-testid={`quote-pdf-btn-${r.id}`}
                className="text-[10.5px] tracking-wider uppercase border border-navy px-2.5 py-1 text-navy hover:bg-navy hover:text-ivory transition-colors"
              >
                PDF öffnen
              </button>
              {(r.status === "approved" || r.status === "declined") && (
                <button
                  onClick={() => {
                    const base = process.env.REACT_APP_BACKEND_URL || "";
                    window.open(`${base}/api/quotes/${r.id}/signature-certificate`, "_blank", "noopener");
                  }}
                  data-testid={`quote-certificate-btn-${r.id}`}
                  className="text-[10.5px] tracking-wider uppercase border border-gold/70 px-2.5 py-1 text-gold hover:bg-gold hover:text-ivory transition-colors"
                  title="Signatur-Zertifikat für das Archiv (PDF)"
                >
                  Zertifikat
                </button>
              )}
              {r.status === "approved" && (
                r.invoice_id ? (
                  <span
                    data-testid={`quote-invoice-done-${r.id}`}
                    className="text-[10px] tracking-wider normal-case text-navy-dark/60"
                    title="Bereits umgewandelt"
                  >
                    ✓ Rechnung erstellt
                  </span>
                ) : (
                  <button
                    onClick={() => convertToInvoice(r)}
                    data-testid={`quote-to-invoice-btn-${r.id}`}
                    disabled={convertingId === r.id}
                    className="text-[10.5px] tracking-wider uppercase border border-gold px-2.5 py-1 text-navy hover:bg-gold hover:text-ivory transition-colors disabled:opacity-50"
                  >
                    {convertingId === r.id ? "…" : "Rechnung erstellen"}
                  </button>
                )
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
